import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import PageHeader from '../../components/common/PageHeader';
import { FaChartLine, FaShieldAlt, FaUsers, FaLightbulb } from 'react-icons/fa';

const WealthManagementPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const breadcrumbs = [
    { label: 'Services', link: '/services' },
    { label: 'Wealth Management' }
  ];

  const services = [
    {
      icon: <FaChartLine className="w-8 h-8 text-secondary" />,
      title: "Investment Strategy",
      description: "Customized investment strategies designed to meet your specific financial goals and risk tolerance."
    },
    {
      icon: <FaShieldAlt className="w-8 h-8 text-secondary" />,
      title: "Risk Management",
      description: "Comprehensive risk assessment and management to protect and preserve your wealth."
    },
    {
      icon: <FaUsers className="w-8 h-8 text-secondary" />,
      title: "Portfolio Diversification",
      description: "Strategic diversification across asset classes to optimize returns and minimize risk."
    },
    {
      icon: <FaLightbulb className="w-8 h-8 text-secondary" />,
      title: "Financial Insights",
      description: "Expert market analysis and insights to guide your investment decisions."
    }
  ];

  return (
    <>
      <PageHeader 
        title="Wealth Management" 
        breadcrumbs={breadcrumbs}
        backgroundImage="/assets/person-office-analyzing-checking-finance-graphs.jpg"
      />
      
      <section className="py-16 md:py-24 bg-background">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-heading font-bold text-primary mb-6">
                Trusted Wealth Management Solutions
              </h2>
              <p className="text-lg text-gray-700 mb-6">
                <span className="font-semibold">Segway Capital</span> provides comprehensive wealth management services 
                that focus on delivering investment solutions for our diverse range of clients. Our approach is 
                based on broad and accurate insight of financial markets, combined with personalized strategies 
                tailored to your unique financial situation.
              </p>
              <p className="text-lg text-gray-700">
                We believe that successful wealth management requires more than just investment expertise—it 
                requires understanding your goals, your timeline, and your tolerance for risk. Our team works 
                closely with you to develop and implement strategies that align with your objectives.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
            >
              <img 
                src="/assets/business-team-working-together.jpg" 
                alt="Wealth Management Team" 
                className="rounded-xl shadow-custom w-full h-full object-cover"
              />
            </motion.div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl p-6 shadow-custom hover:shadow-custom-lg transition-all duration-300"
              >
                <div className="bg-secondary/10 p-4 rounded-full w-16 h-16 flex items-center justify-center mb-6">
                  {service.icon}
                </div>
                <h3 className="text-xl font-heading font-semibold mb-4 text-primary">{service.title}</h3>
                <p className="text-gray-600">{service.description}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
            className="bg-primary text-white rounded-2xl p-8 md:p-12"
          >
            <div className="text-center">
              <h2 className="text-3xl font-heading font-bold mb-6">Ready to Start Your Wealth Management Journey?</h2>
              <p className="text-xl text-white/90 mb-8 max-w-3xl mx-auto">
                Contact our wealth management experts today to discuss how we can help you achieve your financial goals 
                and build lasting prosperity.
              </p>
              <a 
                href="/contact-us" 
                className="btn btn-secondary text-lg py-4 px-8 shadow-xl hover:shadow-secondary/20 btn-3d inline-block"
              >
                Schedule a Consultation
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default WealthManagementPage; 